import React, { ReactNode } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Avatar,
  Divider,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  IconButton,
  Heading,
  Badge,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

interface AdminLayoutProps {
  children: ReactNode;
}

interface SidebarItem {
  name: string;
  icon: any;
  href: string;
  badge?: string;
}

const sidebarItems: SidebarItem[] = [
  { name: 'Dashboard', icon: '🏠', href: '/' },
  { name: 'Series', icon: '📚', href: '/series' },
  { name: 'Subjects', icon: '📝', href: '/subjects' },
  { name: 'Classes', icon: '🎓', href: '/classes' },
  { name: 'Books', icon: '📕', href: '/books' },
  { name: 'Book Types', icon: '📁', href: '/book-types' },
  { name: 'File Upload', icon: '⬆️', href: '/files' },
  { name: 'View All', icon: '👁️', href: '/view' },
  { name: 'Hierarchy', icon: '🌳', href: '/hierarchy' },
  { name: 'App Metadata', icon: '📱', href: '/app-metadata' },
  { name: 'Settings', icon: '⚙️', href: '/settings' },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.900');
  const sidebarBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.100', 'gray.900')}>
      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="xl" fontWeight="bold" color="blue.600">
              📚 BookHub Admin
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <SidebarContent onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop Sidebar */}
      <Box
        position="fixed"
        left={0}
        top={0}
        w="260px"
        h="100vh"
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        display={{ base: 'none', md: 'block' }}
        overflowY="auto"
      >
        <SidebarContent />
      </Box>

      {/* Main Content */}
      <Box ml={{ base: 0, md: '260px' }}>
        {/* Top Navigation */}
        <Flex
          as="header"
          h="16"
          alignItems="center"
          justifyContent="space-between"
          px={6}
          bg={bgColor}
          borderBottom="1px"
          borderColor={borderColor}
          shadow="sm"
        >
          <HStack>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              onClick={onOpen}
              variant="outline"
              aria-label="Open menu"
              icon={<Text fontSize="lg">☰</Text>}
            />
            <Heading size="lg" color="gray.700">
              Educational Content Management
            </Heading>
          </HStack>

          <HStack spacing={4}>
            <Badge colorScheme="green" variant="subtle">
              Backend Connected
            </Badge>
            <Avatar size="sm" name="Admin User" bg="blue.500" />
          </HStack>
        </Flex>

        {/* Page Content */}
        <Box p={6}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const SidebarContent: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const location = useLocation();

  return (
    <VStack spacing={0} align="stretch" h="full">
      {/* Logo */}
      <Box p={6}>
        <Text fontSize="xl" fontWeight="bold" color="blue.600">
          📚 BookHub Admin
        </Text>
        <Text fontSize="sm" color="gray.500">
          Content Management System
        </Text>
      </Box>

      <Divider />

      {/* Navigation Items */}
      <VStack spacing={1} align="stretch" p={4} flex={1}>
        {sidebarItems.map((item) => (
          <Button
            key={item.href}
            as={RouterLink}
            to={item.href}
            variant={location.pathname === item.href ? 'solid' : 'ghost'}
            colorScheme={location.pathname === item.href ? 'blue' : 'gray'}
            justifyContent="flex-start"
            leftIcon={<Text mr={2}>{item.icon}</Text>}
            size="md"
            w="full"
            onClick={onClose}
            rightIcon={
              item.badge ? (
                <Badge size="sm" colorScheme="red">
                  {item.badge}
                </Badge>
              ) : undefined
            }
          >
            {item.name}
          </Button>
        ))}
      </VStack>

      <Divider />

      {/* Footer */}
      <Box p={4}>
        <Text fontSize="xs" color="gray.500" textAlign="center">
          © 2024 BookHub Admin
        </Text>
      </Box>
    </VStack>
  );
};

export default AdminLayout; 